import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Users, AlertCircle, FileText, Copy, MessageSquare } from 'lucide-react';
import { getSessionSummaryStats } from '../services/reportService';
import { SessionSummaryStats } from '../types';
import DocumentViewer from '../components/DocumentViewer';
import toast from 'react-hot-toast';

const SessionSummary: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [sessionSummary, setSessionSummary] = useState<SessionSummaryStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadSessionSummary();
  }, [id]);

  const loadSessionSummary = async () => {
    if (!id) return;

    try {
      setIsLoading(true);
      setError(null);
      const summary = await getSessionSummaryStats(id);

      // Ensure documents array exists and has valid URLs
      if (summary.documents) {
        summary.documents = summary.documents.filter((doc: any) => doc && doc.url);
      }

      setSessionSummary(summary);
    } catch (err: any) {
      console.error('Error loading session summary:', err);
      setError(err.message || 'Failed to load session summary');
    } finally {
      setIsLoading(false);
    }
  };

  const generateWhatsAppMessage = (): string => {
    if (!sessionSummary) return '';

    const sessionDate = new Date(sessionSummary.date);
    const formattedDate = sessionDate.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    let message = `*MBFC Academy - Session Summary*\n\n`;
    message += `📅 Date: ${formattedDate}\n`;
    message += `⏰ Time: ${sessionSummary.time}\n`;
    message += `📍 Location: ${sessionSummary.location}\n`;
    message += `🏃‍♂️ Type: ${sessionSummary.type}\n`;
    message += `🏟️ Ground: ${sessionSummary.groundBooking}\n\n`;

    sessionSummary.squadStats.forEach((squad: any) => {
      message += `*${squad.displayName}*\n`;
      message += `✅ Present: ${squad.attendance.present}\n`;
      message += `❌ Absent: ${squad.attendance.absent}\n`;
      message += `📊 Attendance: ${squad.attendance.percentage.toFixed(1)}%\n`;
      message += `👨‍🏫 Primary Coach: ${squad.coaches.primary}\n`;
      message += `👨‍🏫 Secondary Coach: ${squad.coaches.secondary}\n`;

      // Add session plan if available
      if (sessionSummary.sessionPlans && sessionSummary.sessionPlans[squad.squad]) {
        message += `📝 Session Plan:\n${sessionSummary.sessionPlans[squad.squad]}\n`;
      }

      // Add trial players if any
      if (sessionSummary.trialPlayers && sessionSummary.trialPlayers.length > 0) {
        message += `\n*Trial Players*\n`;
        sessionSummary.trialPlayers.forEach((player: any) => {
          message += `- Name: ${player.name}\n`;
          message += `  Comments: ${player.comments}\n`;
        });
      }

      message += '\n';
    });

    return message;
  };

  const copyToClipboard = () => {
    const message = generateWhatsAppMessage();
    navigator.clipboard.writeText(message)
      .then(() => toast.success('Copied to clipboard!'))
      .catch(() => toast.error('Failed to copy message'));
  };

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <p className="text-gray-500">Loading session summary...</p>
        </div>
      </div>
    );
  }

  if (error || !sessionSummary) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <AlertCircle className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error || 'Session not found'}</p>
              <button
                className="mt-2 text-sm font-medium text-red-700 hover:text-red-600"
                onClick={() => navigate('/sessions')}
              >
                Back to Sessions
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const sessionDate = new Date(sessionSummary.date);
  const formattedDate = sessionDate.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
      <div className="mb-8">
        <button
          type="button"
          className="flex items-center text-navy-800 hover:text-navy-900 mb-2"
          onClick={() => navigate('/sessions')}
        >
          <ArrowLeft size={20} className="mr-2" />
          Back to Sessions
        </button>
        <h1 className="text-3xl font-bold text-gray-900">Session Summary</h1>
      </div>

      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          {/* Session Details */}
          <div className="mb-8">
            <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <Calendar size={20} className="mr-2 text-navy-800" />
              Session Details
            </h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <p className="text-sm text-gray-600">Date: {formattedDate}</p>
                <p className="text-sm text-gray-600">Time: {sessionSummary.time}</p>
                <p className="text-sm text-gray-600">Location: {sessionSummary.location}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Type: {sessionSummary.type}</p>
                <p className="text-sm text-gray-600">Ground: {sessionSummary.groundBooking}</p>
                <p className="text-sm text-gray-600">Duration: {sessionSummary.duration} hours</p>
              </div>
            </div>
          </div>

          {/* Squad Details */}
          <div className="mb-8">
            <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <Users size={20} className="mr-2 text-navy-800" />
              Squad Details
            </h2>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {sessionSummary.squadStats.map((squad: any) => (
                <div key={squad.squad} className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-md font-medium text-gray-900 mb-2">{squad.displayName}</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">Present:</span>
                      <span className="text-sm font-medium text-green-600">{squad.attendance.present}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">Absent:</span>
                      <span className="text-sm font-medium text-red-600">{squad.attendance.absent}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">Attendance:</span>
                      <span className="text-sm font-medium text-navy-600">
                        {squad.attendance.percentage.toFixed(1)}%
                      </span>
                    </div>
                    <div className="pt-2 border-t border-gray-200">
                      <p className="text-sm text-gray-500">
                        <span className="font-medium">Primary Coach:</span><br />
                        {squad.coaches.primary}
                      </p>
                      <p className="text-sm text-gray-500 mt-1">
                        <span className="font-medium">Secondary Coach:</span><br />
                        {squad.coaches.secondary}
                      </p>
                    </div>
                    {sessionSummary.sessionPlans && sessionSummary.sessionPlans[squad.squad] && (
                      <div className="pt-2 border-t border-gray-200">
                        <p className="text-sm font-medium text-gray-900 flex items-center">
                          <MessageSquare size={16} className="mr-1" />
                          Session Plan
                        </p>
                        <p className="mt-1 text-sm text-gray-600 whitespace-pre-wrap">
                          {sessionSummary.sessionPlans[squad.squad]}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Trial Players Section */}
          {sessionSummary.trialPlayers && sessionSummary.trialPlayers.length > 0 && (
            <div className="mb-8">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Trial Players</h2>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {sessionSummary.trialPlayers.map((player: any, index: number) => (
                  <div key={index} className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="text-md font-medium text-gray-900 mb-2">{player.name}</h3>
                    <p className="text-sm text-gray-600">{player.comments}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Documents Section */}
          {sessionSummary.documents && sessionSummary.documents.length > 0 && (
            <div className="mb-8">
              <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <FileText size={20} className="mr-2 text-navy-800" />
                Attached Documents
              </h2>
              <DocumentViewer documents={sessionSummary.documents} />
            </div>
          )}

          {/* WhatsApp Message */}
          <div className="mt-8 bg-gray-50 p-4 rounded-lg">
            <div className="flex flex-wrap justify-between items-center gap-2 mb-4">
              <h2 className="text-lg font-medium text-gray-900">WhatsApp Message</h2>
              <button
                onClick={copyToClipboard}
                className="flex items-center px-3 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 text-sm"
              >
                <Copy size={16} className="mr-2" />
                Copy Message
              </button>
            </div>
            <div className="overflow-x-auto">
              <pre className="whitespace-pre-wrap font-mono text-sm text-gray-700 bg-white p-4 rounded-md border border-gray-200 min-w-0">
                {generateWhatsAppMessage()}
              </pre>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionSummary;